export function requireDefined<T>(value: T | null | undefined, message?: string): T {
    if (value === null || value === undefined) {
        let error = "Unexpected null or undefined value";
        if (message) {
            error += ": " + message;
        }
        throwAtCaller(requireDefined, new Error(error));
    }
    return value;
}

export function assert(condition: unknown, message?: string | (() => string)): asserts condition {
    if (!condition) {
        throwAtCaller(assert, new AssertionError(unwrapErrorMessage(message)));
    }
}

/**
 * Shorthand for throw new AssertionError("Error message")
 * Useful because it is an expression instead of a statement.
 * Can be assigned to any value, because it never returns.
 */
export function panic(message?: string | (() => string)): never {
    throwAtCaller(panic, new AssertionError(unwrapErrorMessage(message)));
}

export function todo(message?: string | (() => string)): never {
    throwAtCaller(todo, new UnimplementedError(unwrapErrorMessage(message)));
}

/**
 * A soft assertion that throws an error in non prod environments, including staging,
 * but logs an error in production mode.
 * This is useful for cases where something unexpected happens, but you can still
 * continue in a slightly broken state.
 * For example, if a block has defined a style control with a hardcoded id and that style control
 * is not found, this is definitely a bug, but it's still possible to go on rendering with a fallback value
 * for this style.
 * It is recommented to liberally use soft assertions so that we fail loutly in staging and local environments.
 *
 * A hard assertion would be more approrpriate if we wanted to display some data from an object
 * but that object is null. It's better to throw an assertion error at the place we detect this,
 * instead of continuing and potentially causing an exception in an unrelated part of the code.
 */
export function softAssert(condition: boolean, message?: string | (() => string)): void {
    if (!condition) {
        if (process.env.NODE_ENV !== "production") {
            throwAtCaller(softAssert, new SoftAssertionError(unwrapErrorMessage(message)));
        } else {
            console.error(`Assertion failed: ${unwrapErrorMessage(message)}`);
        }
    }
}

/**
 * Compile time assertion that a value is not possible in the current branch.
 * Useful for making sure that all possible values are handled in a switch statement/if-else chain.
 *
 * @example
 * type Shape = { type: "circle", radius: number } | { type: "square", side: number };
 * function getArea(shape: Shape): number {
 *   if (shape.type === "circle") {
 *     return Math.PI * shape.radius ** 2;
 *   } else if (shape.type === "square") {
 *     return shape.side ** 2;
 *   }
 *   // Will fail at compile time when a new shape is added to the Shape union
 *   assertNever(shape, () => `Unhandled shape type: ${shape}`);
 * }
 */
export function assertNever(value: never, message?: string | (() => string)): never {
    throwAtCaller(
        assertNever,
        new AssertionError(`Unreachable assertion reached for value(${value}): ${unwrapErrorMessage(message)}`),
    );
}

class AssertionError extends Error {
    override readonly name = "AssertionError";
}
class SoftAssertionError extends Error {
    override readonly name = "SoftAssertionError";
}
class UnimplementedError extends Error {
    override readonly name = "UnimplementedError";
}

// Private functions
function unwrapErrorMessage(message?: string | (() => string)): string {
    return typeof message === "function" ? message() : (message ?? "");
}

/**
 * Uses chrome's non-standard Error.captureStackTrace to set the top of the stack trace
 * to start from the caller of the supplied function {@param f}.
 * Useful in assertion functions to make sure that the stack trace shows the spot where the
 * assertion failed, not the place where the assertion was thrown.
 *
 *
 */
export function throwAtCaller(f: (...args: readonly never[]) => unknown, ex: Error): never {
    // @ts-expect-error: Suppressed automatically in TS 5.8 to 6 migration
    if (Error.captureStackTrace) {
        // @ts-expect-error: Suppressed automatically in TS 5.8 to 6 migration
        Error.captureStackTrace(ex, f);
    }
    throw ex;
}
